import React, { useState } from "react";
import { Users, Zap, Crown, X, Camera, Hand, Pencil } from "lucide-react";
import UserLink from "./UserLink";
import ModalOverlay from "./ModalOverlay";
import { KandiBeadImage } from "./BeadSystem";
import { T } from "../theme";

/**
 * BraceletProfileCard — a kandi bracelet's profile, opened from the
 * collection grid or the public scan flow.
 *
 * The bracelet is the hero: the header renders the kandi's REAL bead
 * sequence through BeadSystem (never fabricated dots), on the opaque
 * modalCard surface the design system mandates for lightboxes.
 *
 * Props:
 *   kandi     – { id, name, emoji, colors[], shapes[], scanCount, ownerCount, scannedDate, currentOwner? }
 *   history   – [{ username, userId, date, location, isCurrent? }]
 *   onClaim   – callback when user claims bracelet (optional)
 *   onScan    – callback when user logs a scan (optional)
 *   onClose   – dismiss
 */
function BraceletProfileCard({ kandi, history = [], onClaim, onScan, onEdit, onClose }) {
  const [tab, setTab] = useState("info"); // "info" | "history"
  const [claimLoading, setClaimLoading] = useState(false);

  if (!kandi) return null;

  const handleClaim = async () => {
    if (!onClaim) return;
    setClaimLoading(true);
    await onClaim(kandi.id);
    setClaimLoading(false);
  };

  return (
    <ModalOverlay onClose={onClose} labelledBy="bpc-name" zIndex={999} cardStyle={styles.card}>
      {/* Glow orbs behind the content */}
      <div style={styles.orbPink} aria-hidden />
      <div style={styles.orbCyan} aria-hidden />

      {onClose && (
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close kandi profile">
          <X size={17} />
        </button>
      )}

      {/* ── Header — the bracelet itself leads ── */}
      <div style={styles.header}>
        <BeadString colors={kandi.colors} shapes={kandi.shapes} />

        <div style={styles.emojiWrap} aria-hidden>
          <span style={styles.emoji}>{kandi.emoji}</span>
        </div>

        <h2 id="bpc-name" style={styles.name}>{kandi.name}</h2>
        <span style={styles.idBadge}>{kandi.id}</span>
      </div>

      {/* ── Stats row ── */}
      <div style={styles.statsRow}>
        <StatPill Icon={Zap} label="Scans" value={kandi.scanCount ?? 0} color={T.cyan} />
        <StatPill Icon={Users} label="Owners" value={kandi.ownerCount} color={T.purple} />
        <StatPill Icon={Crown} label="Since" value={kandi.scannedDate} color={T.gold} />
      </div>

      {/* ── Tab toggle ── */}
      <div style={styles.tabBar} role="tablist" aria-label="Kandi details">
        {[["info", "Info"], ["history", "Journey"]].map(([key, labelText]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            style={{ ...styles.tabBtn, ...(tab === key ? styles.tabActive : {}) }}
            onClick={() => setTab(key)}
          >
            {labelText}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div style={styles.tabContent}>
        {tab === "info" && <InfoTab kandi={kandi} />}
        {tab === "history" && <HistoryTab history={history} />}
      </div>

      {/* ── Action buttons ── */}
      {(onScan || onClaim || onEdit) && (
        <div style={styles.actions}>
          {onEdit && (
            <button style={styles.secondaryBtn} onClick={onEdit}>
              <Pencil size={15} /> Edit Beads
            </button>
          )}
          {onScan && (
            <button style={styles.secondaryBtn} onClick={() => onScan(kandi.id)}>
              <Camera size={15} /> Log Scan
            </button>
          )}
          {onClaim && (
            <button
              style={{ ...styles.primaryBtn, ...(claimLoading ? styles.btnDisabled : {}) }}
              onClick={handleClaim}
              disabled={claimLoading}
            >
              <Hand size={15} /> {claimLoading ? "Claiming…" : "Claim Kandi"}
            </button>
          )}
        </div>
      )}
    </ModalOverlay>
  );
}

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

/**
 * BeadString — the bracelet's real bead sequence, rendered through
 * BeadSystem (the single source of truth for bead rendering). Shows the
 * actual bead count, capped at 14 with a "+N" chip for longer bracelets.
 */
function BeadString({ colors = [], shapes = [], size = 26 }) {
  if (!colors.length) return null;
  const MAX = 14;
  const shown = Math.min(colors.length, MAX);
  const rest = colors.length - shown;
  return (
    <div style={bead.row}>
      <div style={bead.string} aria-hidden />
      {Array.from({ length: shown }, (_, i) => (
        <div key={i} style={{ zIndex: 1, display: "flex", flexShrink: 0 }}>
          <KandiBeadImage
            shapeType={shapes[i] || shapes[i % Math.max(shapes.length, 1)] || "pony"}
            color={colors[i]}
            size={size}
          />
        </div>
      ))}
      {rest > 0 && <span style={bead.more}>+{rest}</span>}
    </div>
  );
}

function StatPill({ Icon, label, value, color }) {
  return (
    <div style={{ ...pill.wrap, borderColor: color + "40" }}>
      <Icon size={15} color={color} style={{ filter: `drop-shadow(0 0 5px ${color})` }} aria-hidden />
      <span style={{ ...pill.value, color }}>{value}</span>
      <span style={pill.label}>{label}</span>
    </div>
  );
}

function InfoTab({ kandi }) {
  return (
    <div>
      {kandi.currentOwner && (
        <div style={info.ownerRow}>
          <Crown size={19} color={T.gold} style={{ filter: `drop-shadow(0 0 6px ${T.gold})`, flexShrink: 0 }} aria-hidden />
          <div>
            <p style={info.ownerLabel}>Current Owner</p>
            <p style={info.ownerName}>
              <UserLink username={typeof kandi.currentOwner === "string" ? kandi.currentOwner : kandi.currentOwner?.username} />
            </p>
          </div>
        </div>
      )}

      <p style={info.blurb}>
        {kandi.colors?.length || 0} {kandi.colors?.length === 1 ? "bead" : "beads"} strung
        with intention — scan it at the next fest to add a chapter to its story.
      </p>

      <div style={info.plurRow}>
        {["Peace ✌️", "Love 💖", "Unity 🤝", "Respect 🙏"].map((v) => (
          <span key={v} style={info.plurTag}>{v}</span>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ history }) {
  if (!history.length) {
    return <p style={hist.empty}>No journey recorded yet — be the first! 🌟</p>;
  }
  return (
    <div style={hist.list}>
      {history.map((h, i) => (
        <div key={i} style={hist.item}>
          <div style={{ ...hist.dot, ...(h.isCurrent ? hist.dotCurrent : {}) }} aria-hidden />
          {i < history.length - 1 && <div style={hist.line} aria-hidden />}
          <div style={hist.content}>
            <span style={hist.username}>
              {h.isCurrent && <Crown size={12} color={T.gold} style={{ verticalAlign: -1, marginRight: 5 }} aria-label="Current owner" />}
              <UserLink username={h.username} userId={h.userId} />
            </span>
            <span style={hist.meta}>
              {h.location && h.location !== "Unknown location" ? `${h.location} · ` : ""}{h.date}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Styles — all tokens from theme.js
───────────────────────────────────────────── */

const styles = {
  card: {
    position: "relative",
    background: T.modalCard,
    border: T.border,
    borderRadius: T.radiusLg,
    padding: "36px 32px 28px",
    maxWidth: "480px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px ${T.purple}26`,
    color: T.textPrimary,
  },
  orbPink: {
    position: "absolute", width: 300, height: 300, borderRadius: "50%",
    background: `${T.pink}2e`, filter: "blur(60px)",
    top: -80, right: -80, pointerEvents: "none",
  },
  orbCyan: {
    position: "absolute", width: 250, height: 250, borderRadius: "50%",
    background: `${T.cyan}1f`, filter: "blur(60px)",
    bottom: -60, left: -60, pointerEvents: "none",
  },
  closeBtn: {
    position: "absolute", top: 12, right: 12,
    background: "rgba(255,255,255,0.08)",
    border: T.borderSoft,
    borderRadius: "50%",
    width: 44, height: 44,
    color: "rgba(255,255,255,0.7)",
    cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.2s",
    zIndex: 10,
  },
  header: { textAlign: "center", marginBottom: 24 },
  emojiWrap: {
    width: 80, height: 80, borderRadius: "50%",
    background: `linear-gradient(135deg, ${T.purple}4d, ${T.pink}4d)`,
    border: "1px solid rgba(255,255,255,0.15)",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "16px auto 14px",
    boxShadow: `0 0 30px ${T.purple}59`,
  },
  emoji: { fontSize: "2.4rem" },
  name: {
    fontFamily: "var(--font-display)",
    fontSize: "1.6rem", fontWeight: 700, margin: "0 0 8px",
    color: T.pink,
    textShadow: `0 0 22px ${T.purple}70`,
  },
  idBadge: {
    display: "inline-block", padding: "4px 14px", borderRadius: 20,
    background: `${T.purple}26`, border: `1px solid ${T.purple}4d`,
    color: T.textStrong, fontSize: "0.8rem",
    fontFamily: "monospace", letterSpacing: "0.05em",
  },
  statsRow: { display: "flex", gap: 10, marginBottom: 20 },
  tabBar: {
    display: "flex", background: T.cardSoft, borderRadius: 12,
    padding: 4, gap: 4, marginBottom: 20,
  },
  tabBtn: {
    flex: 1, padding: "10px", minHeight: 44, border: "1px solid transparent", borderRadius: 9,
    background: "transparent", color: T.textStrong,
    fontWeight: 600, fontSize: "0.95rem", cursor: "pointer",
    transition: "all 0.25s",
  },
  tabActive: {
    background: `linear-gradient(135deg, ${T.pink}4d, ${T.purple}4d)`,
    color: "#fff",
    boxShadow: `0 2px 12px ${T.pink}33`,
    border: `1px solid ${T.purple}40`,
  },
  tabContent: { minHeight: 140, marginBottom: 20 },
  actions: { display: "flex", gap: 12 },
  primaryBtn: {
    flex: 1, padding: 13, minHeight: 44, border: "none", borderRadius: 12,
    background: `linear-gradient(135deg, ${T.pink}, ${T.purple})`,
    color: "#fff", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer",
    boxShadow: `0 4px 20px ${T.pink}66`,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
    transition: "all 0.25s",
  },
  secondaryBtn: {
    flex: 1, padding: 13, minHeight: 44,
    border: T.borderSoft, borderRadius: 12,
    background: T.cardSoft, color: T.textStrong,
    fontSize: "0.95rem", fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
    transition: "all 0.25s",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};

const bead = {
  row: {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 5, position: "relative", minHeight: 30, flexWrap: "nowrap",
    overflow: "hidden", padding: "2px 0",
  },
  string: {
    position: "absolute", left: "8%", right: "8%", top: "50%",
    height: 1.5, background: "rgba(255,255,255,0.12)", borderRadius: 2,
  },
  more: {
    zIndex: 1, flexShrink: 0, padding: "2px 8px", borderRadius: 12,
    background: "rgba(255,255,255,0.08)", border: T.border,
    color: T.textStrong, fontSize: "0.68rem", fontWeight: 700,
  },
};

const pill = {
  wrap: {
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
    gap: 3, padding: "10px 6px",
    background: T.card, border: "1px solid", borderRadius: 12,
  },
  value: { fontSize: "1rem", fontWeight: 700 },
  label: {
    fontSize: "0.7rem", color: T.textMuted,
    textTransform: "uppercase", letterSpacing: "0.05em",
  },
};

const info = {
  blurb: {
    margin: "0 0 16px", color: T.textStrong,
    fontSize: "0.9rem", lineHeight: 1.5,
  },
  ownerRow: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 16px",
    background: `${T.purple}1a`, border: `1px solid ${T.purple}33`,
    borderRadius: 12, marginBottom: 14,
  },
  ownerLabel: { margin: 0, fontSize: "0.75rem", color: T.textMuted },
  ownerName: { margin: 0, fontWeight: 700 },
  plurRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  plurTag: {
    padding: "5px 10px", borderRadius: 20,
    background: "rgba(255,255,255,0.06)", border: T.borderSoft,
    fontSize: "0.78rem", color: T.textStrong,
  },
};

const hist = {
  list: { display: "flex", flexDirection: "column", gap: 0 },
  item: {
    display: "flex", alignItems: "flex-start", gap: 12,
    position: "relative", paddingBottom: 14,
  },
  dot: {
    width: 12, height: 12, borderRadius: "50%",
    background: "rgba(255,255,255,0.25)",
    border: "2px solid rgba(255,255,255,0.2)",
    marginTop: 4, flexShrink: 0,
  },
  dotCurrent: {
    background: `linear-gradient(135deg, ${T.pink}, ${T.purple})`,
    boxShadow: `0 0 8px ${T.pink}80`,
  },
  line: {
    position: "absolute", left: 5, top: 14, bottom: 0,
    width: 2, background: "rgba(255,255,255,0.08)",
  },
  content: { display: "flex", flexDirection: "column", gap: 2 },
  username: { fontWeight: 600, fontSize: "0.95rem", color: T.textPrimary },
  meta: { fontSize: "0.8rem", color: T.textMuted },
  empty: {
    textAlign: "center", color: T.textStrong,
    padding: "30px 0", fontSize: "0.95rem",
  },
};

// BeadString is the shared real-bead row renderer — import it from here
// rather than duplicating it (used by PhotoViewerModal and others).
export { BeadString };
export default BraceletProfileCard;
